// JOBE-0044.10: PredicatePicker — the 380 px popover with header,
// search input, and grouped option list. Reads vocabulary from
// `lib/omni-parser/vocabulary.js` (JOBE-0098.2's canonical source).
//
// The "Schema fields" group row is disabled unless exactly one
// `type:` chip is active (per spec's pinned decision).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FRIENDLY_VOCABULARY,
  GROUP_ORDER,
  GROUP_SUBTITLES,
  RETIRED_TOKENS,
} from '../../lib/omni-parser/vocabulary.js';
import { useFacetSearch } from './useFacetSearch.js';

const SCHEMA_FIELDS_ROW_TOOLTIP =
  'Filter by a single type to enable schema-field facets.';

// Group entries from FRIENDLY_VOCABULARY by `.group`, preserving order.
function groupEntries(entries) {
  const buckets = new Map();
  for (const e of entries) {
    if (!buckets.has(e.group)) buckets.set(e.group, []);
    buckets.get(e.group).push(e);
  }
  return GROUP_ORDER.filter((g) => buckets.has(g))
    .map((g) => ({ group: g, entries: buckets.get(g) }));
}

export function PredicatePicker({
  activeTypeValues,
  onPickPredicate,
  onPickSchemaField,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const searchRef = useRef(null);

  // Filter retired tokens defensively even though FRIENDLY_VOCABULARY
  // excludes them by construction — defence in depth for the
  // retired-tokens-absent test contract.
  const safeEntries = useMemo(
    () => FRIENDLY_VOCABULARY.filter(
      (e) => !RETIRED_TOKENS.has(e.friendlyToken)
        && !RETIRED_TOKENS.has(e.wirePredicate),
    ),
    [],
  );

  const filtered = useFacetSearch(safeEntries, query);
  const grouped = useMemo(() => groupEntries(filtered), [filtered]);

  // Flat list of focusable rows for keyboard nav (vocabulary rows
  // followed by the synthetic schema-fields row).
  const schemaFieldsEnabled = Array.isArray(activeTypeValues)
    && activeTypeValues.length === 1;
  const showSchemaRow = !query.trim()
    || 'schema fields'.includes(query.trim().toLowerCase())
    || 'field'.includes(query.trim().toLowerCase());

  const focusableRows = useMemo(() => {
    const rows = [];
    for (const g of grouped) {
      for (const e of g.entries) {
        rows.push({ kind: 'predicate', entry: e });
      }
    }
    if (showSchemaRow) {
      rows.push({ kind: 'schemafields', enabled: schemaFieldsEnabled });
    }
    return rows;
  }, [grouped, schemaFieldsEnabled, showSchemaRow]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    // Reset highlight when the filter set changes shape.
    setHighlight(0);
  }, [query]);

  const choose = (idx) => {
    const row = focusableRows[idx];
    if (!row) return;
    if (row.kind === 'schemafields') {
      if (!row.enabled) return;
      if (onPickSchemaField) onPickSchemaField();
      return;
    }
    if (onPickPredicate) onPickPredicate(row.entry);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onClose) onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, focusableRows.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      choose(highlight);
      return;
    }
    // Cmd+/ or Ctrl+/ → focus the search input.
    if ((e.metaKey || e.ctrlKey) && e.key === '/') {
      e.preventDefault();
      searchRef.current?.focus();
    }
  };

  // Pre-compute the predicate-row → flat-index lookup so the render
  // pass doesn't rely on a mutable counter (PR #693 review NIT-1).
  // `focusableRows` already holds the canonical row order: predicates
  // first in group order, schemafields row last (if shown). The schema
  // row's index is therefore `focusableRows.length - 1` when present.
  const indexByPredicateToken = useMemo(() => {
    const m = new Map();
    focusableRows.forEach((r, i) => {
      if (r.kind === 'predicate') m.set(r.entry.friendlyToken, i);
    });
    return m;
  }, [focusableRows]);
  const schemaRowIdx = showSchemaRow ? focusableRows.length - 1 : -1;

  const rowRenderer = (entry) => {
    const myIdx = indexByPredicateToken.get(entry.friendlyToken);
    const isHi = myIdx === highlight;
    return (
      <li key={`${entry.friendlyToken}`}>
        <button
          type="button"
          className={
            'library-composer-picker-row '
            + (isHi ? 'library-composer-picker-row-hi' : '')
          }
          onClick={() => choose(myIdx)}
          onMouseEnter={() => setHighlight(myIdx)}
          data-friendly={entry.friendlyToken}
        >
          <span className="library-composer-picker-text">
            <span className="library-composer-picker-label">{entry.label}</span>
            <span className="library-composer-picker-gloss">{entry.gloss}</span>
          </span>
          <span className="library-composer-picker-raw">{entry.wirePredicate}</span>
        </button>
      </li>
    );
  };

  return (
    <div
      className="library-composer-popover library-composer-picker"
      role="dialog"
      aria-label="Add a filter"
      onKeyDown={onKeyDown}
    >
      <header className="library-composer-picker-head">
        <h3 className="library-composer-picker-title">Add a filter</h3>
        <span className="library-composer-picker-hint">Esc to close</span>
      </header>
      <div className="library-composer-picker-search-wrap">
        <input
          ref={searchRef}
          type="text"
          className="library-composer-search"
          placeholder="Find a facet…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Find a facet"
        />
        <span className="library-composer-picker-search-hotkey">⌘ /</span>
      </div>
      <div className="library-composer-picker-groups">
        {grouped.length === 0 && !showSchemaRow && (
          <p className="library-composer-empty">no matches</p>
        )}
        {grouped.map(({ group, entries }) => (
          <section key={group} className="library-composer-picker-group">
            <div className="library-composer-picker-gh">
              <span className="library-composer-picker-gh-title">{group}</span>
              <span className="library-composer-picker-gh-sub">
                {GROUP_SUBTITLES[group]}
              </span>
            </div>
            <ul className="library-composer-picker-list">
              {entries.map((e) => rowRenderer(e))}
            </ul>
          </section>
        ))}
        {showSchemaRow && (
          <section className="library-composer-picker-group">
            <div className="library-composer-picker-gh">
              <span className="library-composer-picker-gh-title">Schema fields</span>
              <span className="library-composer-picker-gh-sub">
                per selected type
              </span>
            </div>
            <ul className="library-composer-picker-list">
              <li>
                <button
                  type="button"
                  className={
                    'library-composer-picker-row '
                    + (schemaRowIdx === highlight
                      ? 'library-composer-picker-row-hi ' : '')
                    + (schemaFieldsEnabled ? '' : 'library-composer-picker-row-disabled')
                  }
                  onClick={() => choose(schemaRowIdx)}
                  onMouseEnter={() => setHighlight(schemaRowIdx)}
                  disabled={!schemaFieldsEnabled}
                  title={schemaFieldsEnabled ? undefined : SCHEMA_FIELDS_ROW_TOOLTIP}
                  aria-disabled={!schemaFieldsEnabled}
                  data-friendly="field"
                >
                  <span className="library-composer-picker-text">
                    <span className="library-composer-picker-label">Schema field</span>
                    <span className="library-composer-picker-gloss">
                      {schemaFieldsEnabled
                        ? 'scalar attribute of the selected type'
                        : 'select one type first'}
                    </span>
                  </span>
                  <span className="library-composer-picker-raw">field.*</span>
                </button>
              </li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
